import React from 'react';
import { Box, Button, Card, CardContent, CardMedia, Chip, Link, Stack, Switch, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { OptionalService } from '../../types';
import { GetPAXTotalsResponse, renderDescription, separadorDeMiles } from '../../helpers';

interface Props {
	optionalService: OptionalService;
	paxTotals: GetPAXTotalsResponse;
	selectable?: boolean;
	selected?: boolean;
	onToggle?: () => void;
}

export const OptionalServiceCardDesktop = (props: Props): JSX.Element => {
	const { optionalService, paxTotals, selectable, selected, onToggle } = props;
	const { isHtml, content } = renderDescription(optionalService.description);
	const prices = [
		optionalService.adultPrice
			? {
					label: 'Adulto',
					value: `USD ${separadorDeMiles(optionalService.adultPrice)}`,
			  }
			: null,
		paxTotals.child > 0 && optionalService.childPrice != undefined
			? {
					label: 'Niño',
					value: `USD ${separadorDeMiles(optionalService.childPrice)}`,
			  }
			: null,
		paxTotals.infants > 0 && optionalService.infantPrice != undefined
			? {
					label: 'Bebé',
					value: `USD ${separadorDeMiles(optionalService.infantPrice)}`,
			  }
			: null,
	].filter(Boolean) as { label: string; value: string }[];

	return (
		<Card
			sx={{
				display: 'grid',
				gridTemplateColumns: { sm: '220px 1fr' },
				borderRadius: '12px',
				overflow: 'hidden',
				border: selected ? '2px solid' : '1px solid rgba(49, 25, 75, 0.10)',
				borderColor: selected ? 'secondary.main' : undefined,
				boxShadow: selected ? '0 12px 28px rgba(237, 102, 2, 0.16)' : '0 6px 18px rgba(49, 25, 75, 0.08)',
			}}
		>
			<CardMedia
				image={optionalService.imageUrl}
				title={optionalService.name}
				sx={{
					minHeight: 190,
					backgroundColor: '#f2eef6',
				}}
			/>
			<Box display="flex" flexDirection="column" minWidth={0}>
				<CardContent sx={{ p: 2, flex: 1 }}>
					<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
						<Box minWidth={0}>
							<Typography variant="overline" color="secondary" fontWeight={900} lineHeight={1}>
								Opcional
							</Typography>
							<Typography variant="h6" color="primary" fontWeight={900} lineHeight={1.15}>
								{optionalService.name}
							</Typography>
						</Box>
						{selectable && (
							<Box textAlign="right">
								<Switch color="secondary" checked={Boolean(selected)} onChange={onToggle} />
								<Typography
									variant="caption"
									color={selected ? 'secondary' : 'text.secondary'}
									fontWeight={900}
									display="block"
								>
									{selected ? 'Agregado' : 'Agregar'}
								</Typography>
							</Box>
						)}
					</Box>

					<Box
						mt={1.25}
						sx={{
							fontFamily: 'Montserrat, sans-serif',
							color: 'text.secondary',
							fontSize: '0.88rem',
							lineHeight: 1.45,
							'& ul': {
								paddingLeft: '18px',
								marginTop: 0,
								marginBottom: 0,
							},
							'& li': {
								fontSize: '0.88rem',
								lineHeight: 1.45,
							},
						}}
					>
						{isHtml ? (
							<div dangerouslySetInnerHTML={{ __html: content }} />
						) : (
							<Typography style={{ whiteSpace: 'pre-line' }} component="span" variant="body2">
								{content}
							</Typography>
						)}
					</Box>
				</CardContent>

				<Box
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					gap={1.5}
					flexWrap="wrap"
					sx={{
						px: 2,
						py: 1.25,
						borderTop: '1px solid rgba(49, 25, 75, 0.08)',
						backgroundColor: 'rgba(49, 25, 75, 0.025)',
					}}
				>
					{/* MUI 5.6.4 no soporta useFlexGap todavía (llegó en 5.10): con spacing
					    los chips que envuelven quedan con margen negativo. `gap` en sx da
					    el comportamiento que se buscaba. */}
					<Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
						{prices.map((price) => (
							<Chip
								key={price.label}
								label={`${price.label}: ${price.value}`}
								size="small"
								sx={{
									borderRadius: '999px',
									fontWeight: 900,
									color: 'secondary.main',
									backgroundColor: 'white',
									border: '1px solid rgba(237, 102, 2, 0.18)',
								}}
							/>
						))}
					</Stack>
					{optionalService.url && (
						<Link href={optionalService.url} color="inherit" target="_blank" rel="noopener" underline="none">
							<Button
								variant="text"
								size="small"
								endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
								sx={{
									borderRadius: '999px',
									textTransform: 'none',
									fontWeight: 900,
									color: 'primary.main',
									backgroundColor: 'rgba(49, 25, 75, 0.055)',
									'&:hover': {
										backgroundColor: 'rgba(49, 25, 75, 0.085)',
									},
								}}
							>
								Ver detalles
							</Button>
						</Link>
					)}
				</Box>
			</Box>
		</Card>
	);
};
